import { apiRequest, getApiBaseUrl } from "@/shared/lib/apiClient";
import { getAuthToken } from "../auth";
import { workContextHeaders } from "@/modules/safecheck/workContext";

export async function openHtmlReport(url: string, errorLabel: string): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Connexion requise.");
  }

  // L'onglet s'ouvre pendant le clic (avant tout appel réseau), sinon le navigateur le bloque ;
  // le document y est chargé ensuite. Sans « noopener », qui ferait toujours renvoyer null.
  const popup = window.open("", "_blank");
  if (!popup) {
    throw new Error("Autorisez les pop-ups pour afficher l'impression.");
  }
  popup.opener = null;
  popup.document.write('<p style="font-family:sans-serif;padding:24px;color:#555">Chargement du document…</p>');

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...workContextHeaders(),
        Accept: "text/html",
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || errorLabel);
    }

    const html = await response.text();
    const objectUrl = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    popup.location.href = objectUrl;
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (err) {
    popup.close();
    throw err;
  }
}

export async function openInvoicePrint(invoiceId: number): Promise<void> {
  await openHtmlReport(
    `${getApiBaseUrl()}/invoices/${invoiceId}/print`,
    "Impression impossible.",
  );
}

export async function openInvoiceForfaitReport(
  invoiceId: number,
  pricing: "prestataire" | "client" = "prestataire",
): Promise<void> {
  const qs = pricing === "client" ? "?pricing=client" : "";
  await openHtmlReport(
    `${getApiBaseUrl()}/invoices/${invoiceId}/forfait-report${qs}`,
    "Impression forfait impossible.",
  );
}

export async function openInvoiceAiReport(
  invoiceId: number,
  mode: "revised" | "complete" = "revised",
): Promise<void> {
  const qs = mode === "complete" ? "?mode=complete" : "";
  await openHtmlReport(
    `${getApiBaseUrl()}/invoices/${invoiceId}/ai-report${qs}`,
    "Génération facture IA impossible.",
  );
}

/** Fiche de réfaction et omission d'une facture. */
export async function openRefactionSheet(invoiceId: number): Promise<void> {
  await openHtmlReport(`${getApiBaseUrl()}/invoices/${invoiceId}/refaction-sheet`, "Fiche de réfaction indisponible.");
}

/** Note de paiement d'une facture validée : Sinistre (/claims) ou assureur (/insurer). */
export async function openPaymentNote(invoiceId: number, scope: "claims" | "insurer"): Promise<void> {
  await openHtmlReport(`${getApiBaseUrl()}/${scope}/invoices/${invoiceId}/payment-note`, "Note de paiement indisponible.");
}

/** Fiche de décompte que l'assureur envoie à l'entreprise (compte Assurance). */
export type DecompteList = {
  validated_invoices: number;
  companies: string[];
  batches: string[];
  rows: Array<{ company: string; batch: string; from: string | null; to: string | null; invoices: number; claimed: number; net: number }>;
};

type DecompteFilters = { company?: string; from?: string; to?: string; batch?: string };

function decompteQuery(filters: DecompteFilters): string {
  return new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1]))).toString();
}

/** Décomptes de l'assureur, par entreprise et lot de dépôt. */
export async function fetchDecomptes(filters: DecompteFilters): Promise<DecompteList> {
  return apiRequest<DecompteList>(`/insurer/decomptes?${decompteQuery(filters)}`, { auth: true });
}

/** Fiche de décompte d'une entreprise pour un lot (lot vide = factures sans lot). */
export async function openDecompteSheet(filters: DecompteFilters & { company: string }): Promise<void> {
  const query = new URLSearchParams({ company: filters.company, batch: filters.batch ?? "" });
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  await openHtmlReport(`${getApiBaseUrl()}/insurer/decomptes/print?${query.toString()}`, "Fiche de décompte indisponible.");
}

export async function openDecompte(invoiceId: number): Promise<void> {
  await openHtmlReport(`${getApiBaseUrl()}/insurer/invoices/${invoiceId}/decompte`, "Fiche de décompte indisponible.");
}

/** Mémo de règlement mensuel d'un prestataire (compte Sinistre). */
export async function openSettlementSheet(hospitalId: number, month: string): Promise<void> {
  await openHtmlReport(`${getApiBaseUrl()}/claims/settlements/${hospitalId}/print?month=${month}`, "Bordereau indisponible.");
}

/** Note de réception remise à l'assureur (compte Admin). */
export async function openReceptionNote(insurerId: number, from: string, to: string): Promise<void> {
  await openHtmlReport(
    `${getApiBaseUrl()}/admin/insurers/${insurerId}/reception-note?from=${from}&to=${to}`,
    "Note de réception indisponible.",
  );
}
